import { PaginationProps, TableProps } from 'antd';
import { getApi, postApi } from 'apis';
import ApproveRejectModal from 'components/Global/ApproveRejectModal';
import TableActionHeader from 'components/Global/TableActionHeader';
import ViewPopUpWrapper from 'components/Global/ViewPopUpWrapper';
import dayjs from 'dayjs';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toastText, checkPermission } from 'utils/utils';
import { isCombosEnabled } from 'utils/featureFlags';
import Buttons from 'components/Global/Buttons';
import CustomTable from './CustomTable';
import ViewMedicinesBody from './ViewMedicinesBody';
import ViewComboBody from 'components/Combos/ViewComboBody';
import { useDebounce } from 'utils/hooks';
import styles from './index.module.scss';

type QualityCheckActionType = 'APPROVE' | 'REJECT';

const QualityCheckTable = () => {
	// State variables
	const [title, setTitle] = useState('Quality Check');
	const [search, setSearch] = useState('');
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(
		Number(localStorage.getItem('medicineTableSize')) || 10
	);
	const [filter, setFilter] = useState('');
	const [sort, setSort] = useState('');
	const [type, setType] = useState('asc');
	const [editSelectedMedicine, setEditSelectedMedicine] = useState<any>();
	const [totalMedicines, setTotalMedicines] = useState(0);
	const [medicinesData, setMedicinesData] = useState([]);
	const [medicineQcCount, setMedicineQcCount] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [allUsers, setAllUsers] = useState([]);
	const [selectedUser, setSelectedUser] = useState(null);
	const [selectedDate, setSelectedDate] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
	const [isViewPopUpOpen, setIsViewPopUpOpen] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
	const [qualityCheckType, setQualityCheckType] = useState<string>('');
	const [isInitialized, setIsInitialized] = useState(false);
	const [rejectionReason, setRejectionReason] = useState<string>('');
	const [qcEntity, setQcEntity] = useState<'medicine' | 'combo'>('medicine');

	const debouncedSearch = useDebounce(search, 500);

	const medicinesPermissions = useSelector(
		(state: any) => state?.userProfile?.data
	);

	const canViewMedicineQc = checkPermission(medicinesPermissions?.permissions, {
		permissionName: 'QualityCheck',
		permission: ['view', 'viewAll'],
	});

	const canViewComboQc = checkPermission(medicinesPermissions?.permissions, {
		permissionName: 'ComboQualityCheck',
		permission: ['view', 'viewAll'],
	});

	const effectiveCanViewComboQc = isCombosEnabled() && canViewComboQc;

	const isCombo = qcEntity === 'combo';

	const isApprovalPermission = checkPermission(medicinesPermissions?.permissions, {
		permissionName: isCombo ? 'ComboQualityCheck' : 'QualityCheck',
		permission: ['approval'],
	});

	const openQualityCheckFromView = (type: QualityCheckActionType) => {
		setQualityCheckType(type);
		setIsModalOpen(true);
	};

	const viewHandler = (record: any) => {
		setEditSelectedMedicine(record);
		setTitle(isCombo ? 'Combo Details' : 'Medicine Details');
		setIsViewPopUpOpen(true);
	};


	const performSearchHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setSearch(value);
	};

	useEffect(() => {
		setPage(1);
	}, [debouncedSearch]);

	const fetchMedicines = useCallback(async () => {
		setIsLoading(true);
		const query = {
			page: page,
			limit: limit,
			search: debouncedSearch,
			sort: sort,
			type: type,
			filter: filter,
			userId: selectedUser,
			selectedDate,
			isQualityCheck: true
		};
		try {
			const endpoint = isCombo ? '/combo/get-all' : '/medicine/get-all';
			const res = await getApi(endpoint, query);
			setMedicinesData(res?.data?.data);
			setMedicineQcCount(res?.data?.medicinesWithQCCount)
			setTotalMedicines(res?.data?.total);
		} catch (error: any) {
			toastText(error?.response?.data?.error?.errorDescription, 'error');
		}
		finally {
			setIsLoading(false);
		}
	}, [page, limit, debouncedSearch, type, sort, filter, selectedUser, selectedDate, isCombo]);

	const handleTableChange: TableProps<any>['onChange'] = (
		pagination,
		filters,
		sorter
	) => {
		const sortOrder = Array.isArray(sorter)
			? sorter[0]?.order
			: sorter.order === 'ascend'
				? 'asc'
				: 'desc';
		setType(sortOrder as string);

		const sortField = Array.isArray(sorter)
			? sorter[0]?.field
			: sorter.field === 'name'
				? 'firstName'
				: sorter.field;
		setSort(sortField as string);
	};

	const onShowSizeChange: PaginationProps['onShowSizeChange'] = (
		current,
		pageSize
	) => {
		setPage(current);
		setLimit(pageSize);
	};

	const fetchAllUsers = async () => {
		const isMedicineQcViewAll = checkPermission(medicinesPermissions?.permissions, {
			permissionName: 'QualityCheck',
			permission: ['viewAll'],
		});
		const isComboQcViewAll = checkPermission(medicinesPermissions?.permissions, {
			permissionName: 'ComboQualityCheck',
			permission: ['viewAll'],
		});

		if (medicinesPermissions?.isAdmin === true || isMedicineQcViewAll || isComboQcViewAll) {
			await getApi('/user').then((res: any) => {
				if (res?.data?.responseStatus === 200) {
					const users = res?.data?.data;

					const filteredUsers = users?.map((item: any) => ({
						userId: item.userId,
						name: item.name,
						status: item.status,
					}));

					setAllUsers(filteredUsers);
				}
			}).catch((err: any) => {
			})
		}
	}

	const handleOk = () => {
		setIsModalOpen(false);
		setEditSelectedMedicine(null);
	};

	const handleCancel = () => {
		setIsModalOpen(false);
		setRejectionReason('');
		if (!isViewPopUpOpen) {
			setEditSelectedMedicine(null);
		}
	};

	const closeAfterQualityCheck = () => {
		setIsModalOpen(false);
		setRejectionReason('');
		setIsViewPopUpOpen(false);
		setEditSelectedMedicine(null);
	};

	const qualityCheckHandler = async (qualityCheck: any) => {
		if (editSelectedMedicine) {
			try {
				setDeleteLoading(true);
				const endpoint = isCombo
					? `/combo/quality-check/${editSelectedMedicine.id}`
					: `/medicine/quality-check/${editSelectedMedicine.id}`;
				const resp = await postApi(endpoint, { qualityCheck, rejectionReason });
				toastText(resp?.data?.message ?? 'Product approved successfully', 'success');
			} catch (error: any) {
				toastText(error?.response?.data?.message || `Something went wrong while approving the ${isCombo ? 'combo' : 'medicine'}`, 'error');
			} finally {
				setDeleteLoading(false);
				fetchMedicines();
				closeAfterQualityCheck();
			}
		}
	}

	useEffect(() => {
		const isMedicineQcViewAll = checkPermission(medicinesPermissions?.permissions, {
			permissionName: 'QualityCheck',
			permission: ['viewAll'],
		});
		const isComboQcViewAll =
			isCombosEnabled() &&
			checkPermission(medicinesPermissions?.permissions, {
				permissionName: 'ComboQualityCheck',
				permission: ['viewAll'],
			});
		if (medicinesPermissions && !isInitialized) {
			if (
				medicinesPermissions?.isAdmin === true ||
				isMedicineQcViewAll ||
				isComboQcViewAll
			) {
				setSelectedUser(null);
			} else {
				setSelectedUser(medicinesPermissions?.id);
			}
			if (!canViewMedicineQc && effectiveCanViewComboQc) {
				setQcEntity('combo');
			}
			setIsInitialized(true);
		}
	}, [medicinesPermissions, isInitialized, canViewMedicineQc, effectiveCanViewComboQc]);

	useEffect(() => {
		setPage(1);
	}, [qcEntity]);

	useEffect(() => {
		if (isInitialized) {
			fetchMedicines();
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps -- fetchMedicines is stable
	}, [limit, page, debouncedSearch, sort, type, filter, selectedUser, selectedDate, isInitialized, isCombo]);

	useEffect(() => {
		const isMedicineQcViewAll = checkPermission(medicinesPermissions?.permissions, {
			permissionName: 'QualityCheck',
			permission: ['viewAll'],
		});
		const isComboQcViewAll =
			isCombosEnabled() &&
			checkPermission(medicinesPermissions?.permissions, {
				permissionName: 'ComboQualityCheck',
				permission: ['viewAll'],
			});

		if (
			medicinesPermissions?.isAdmin === true ||
			isMedicineQcViewAll ||
			isComboQcViewAll
		) {
			fetchAllUsers();
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps -- fetchAllUsers is stable
	}, [medicinesPermissions]);

	return (
		<>
			<div >
				<TableActionHeader title="Quality Check">
				</TableActionHeader>

				<CustomTable
					dataSource={medicinesData}
					medicineQcCount = {medicineQcCount}
					handleTableChange={handleTableChange}
					onShowSizeChange={onShowSizeChange}
					isLoading={isLoading}
					search={search}
					totalRecords={totalMedicines}
					performSearchHandler={performSearchHandler}
					performFilterHandler={(value: any) => setFilter(value)}
					setEditSelectedMedicine={setEditSelectedMedicine}
					currentPage={page}
					pageSize={limit}
					setSearch={setSearch}
					setTitle={setTitle}
					allUsers={allUsers}
					setSelectedUser={setSelectedUser}
					selectedUser={selectedUser}
					selectedDate={selectedDate}
					setSelectedDate={setSelectedDate}
					viewHandler={viewHandler}
					setIsModalOpen={setIsModalOpen}
					setQualityCheckType={setQualityCheckType}
					qcEntity={qcEntity}
					setQcEntity={setQcEntity}
					canViewMedicineQc={canViewMedicineQc}
					canViewComboQc={effectiveCanViewComboQc}
				/>
			</div>

			<div>
				{isViewPopUpOpen && (
					<ViewPopUpWrapper
						title={title}
						isPoppedUp={isViewPopUpOpen}
						closeDrawerByAnimation={() => {
							setIsViewPopUpOpen(false);
							setEditSelectedMedicine(null);
							setIsModalOpen(false);
							setRejectionReason('');
						}}
					>
						{isCombo ? (
							<ViewComboBody combo={editSelectedMedicine} />
						) : (
							<ViewMedicinesBody
								medicine={editSelectedMedicine}
							/>
						)}
						{isApprovalPermission && (
							<div className={styles.viewActions}>
								<Buttons
									gap="middle"
									buttons={[
										{
											text: 'Approve',
											isLoading: false,
											className: 'approve-button',
											fontSize: '1.6rem',
											minWidth: '14rem',
											minHeight: '4rem',
											isSubmit: false,
											onclick: () => openQualityCheckFromView('APPROVE'),
										},
										{
											text: 'Reject',
											isLoading: false,
											className: 'remove-button',
											fontSize: '1.6rem',
											minWidth: '14rem',
											minHeight: '4rem',
											isSubmit: false,
											onclick: () => openQualityCheckFromView('REJECT'),
										},
									]}
								/>
							</div>
						)}
					</ViewPopUpWrapper>
				)}
			</div>

			<ApproveRejectModal
				handleCancel={handleCancel}
				handleOk={handleOk}
				isModalOpen={isModalOpen}
				approveHandler={qualityCheckHandler}
				isLoading={deleteLoading}
				selectedApproveData={isCombo ? editSelectedMedicine?.comboName : editSelectedMedicine?.medicineName}
				approveHeading={isCombo ? 'Combo' : 'Medicine'}
				type={qualityCheckType === 'APPROVE' ? 'APPROVE' : 'REJECT'}
				setRejectionReason={setRejectionReason}
				rejectionReason={rejectionReason}
				zIndex={isViewPopUpOpen ? 1100 : undefined}
			/>
		</>
	);
};

export default QualityCheckTable;
